import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { useInterval } from '@elfsight/lib-utils';
import { differenceInHours, isWithinInterval } from 'date-fns';
import { useCurrentDateInTimezoneGetter } from './utils/date-time-format';
import { useUpcomingWorkHoursIntervals } from './utils/use-upcoming-work-hours-intervals';

type CurrentStatusContextValue = {
  isCurrentlyOpen: boolean;
  nextChangeIn?: Date;
  nextChangeWithinTwoHours: boolean;
};

const CurrentStatusContext = createContext<CurrentStatusContextValue>({
  isCurrentlyOpen: false,
  nextChangeIn: new Date(),
  nextChangeWithinTwoHours: false
});

export function CurrentStatusProvider({ children }: PropsWithChildren) {
  const getCurrentDateInTimezone = useCurrentDateInTimezoneGetter();
  const getClosestWorkHoursInterval = useUpcomingWorkHoursIntervals();

  const [isCurrentlyOpen, setIsCurrentlyOpen] = useState(false);
  const [nextChangeIn, setNextChangeIn] = useState<Date>();
  const [nextChangeWithinTwoHours, setNextChangeWithinTwoHours] =
    useState(false);

  const checkStatus = useCallback(() => {
    const now = getCurrentDateInTimezone();

    const closestWorkingHours = getClosestWorkHoursInterval(now);

    if (!closestWorkingHours) {
      setIsCurrentlyOpen(false);
      setNextChangeIn(undefined);
      setNextChangeWithinTwoHours(false);
      return;
    }

    const [nextFrom, nextUntil] = closestWorkingHours;

    const isOpen = isWithinInterval(now, { start: nextFrom, end: nextUntil });
    const nextChangeIn = isOpen ? nextUntil : nextFrom;

    setIsCurrentlyOpen(isOpen);
    setNextChangeIn(nextChangeIn);
    setNextChangeWithinTwoHours(differenceInHours(nextChangeIn, now) < 2);
  }, [getClosestWorkHoursInterval, getCurrentDateInTimezone]);

  useEffect(checkStatus, [checkStatus]);
  useInterval(checkStatus, 1000);

  return (
    <CurrentStatusContext.Provider
      value={{ isCurrentlyOpen, nextChangeIn, nextChangeWithinTwoHours }}
    >
      {children}
    </CurrentStatusContext.Provider>
  );
}

export const useCurrentStatus = () => useContext(CurrentStatusContext);
